import { saveAppliedTruth } from '../shared/applied-truth-transport';
import { inventoryAppliedFrame, pushInventoryTruth } from '../shared/inventory-applied-truth';
import type { ActivityWrite } from '../janus-engine/actions/activity';
// inventory-flag-truth.ts — Lane INV2 S1 / S1b (2026-09-27): the client-truth INVENTORY FLAG subscriber + window seam.
//
// The pure half (janus-engine/inventory/flag-adjust.ts) builds and serializes the commits; callers (the PC sheet's wares tile,
// the object card's wares + hide / reveal buttons) paint optimistically and queue an explicit desired value. This module:
//   * applies the peers' frames (`janus_inventory_flag`, provisional → committed / correction): wares is patched into every
//     cached compiled copy of the row (the caster caches + the encounter members, applyWaresToTree — the PHP patcher's twin);
//     hidden uses saved-row patches; its committed receipt also repairs held facets and the Hide / Reveal buttons;
//   * exposes window.ftCommitInventoryFlag for out-of-bundle callers.
import {
  applyWaresToTree, noteInventoryFlagFrame, queueInventoryFlag, INVENTORY_FLAG_SCHEMA, readInventoryHidden, InventoryFlagPreparationError,
  postInventoryFlag, pendingInventoryFlag,
  type Bit, type InventoryFlagAnswer, type InventoryFlagKind, type InventoryFlagCommand,
} from '../janus-engine/inventory/flag-adjust.js';
import { refreshSavedInventory } from '../shared/inventory-saved-refresh';
import { normalizePhpListish } from '../shared/php-listish.js';
import { planInventoryHidden, INVENTORY_HIDDEN_ID } from '../janus-engine/inventory/hidden-plan.js';
import { HandBack, record, ecId, ecLean, inventoryId, itemField, type Row } from '../janus-engine/ability/context.js';
import { inventoryField, encounterField } from '../shared/array-types.js';
import { statusRows, statusItemId } from '../janus-engine/statuses/expiry.js';
import { isSelfStatusOrigin } from '../janus-engine/statuses/status-origin.js';
import { abilityTruthReaders, hydrateTurnTriggerDefinitions } from './ability-truth.js';
import { actorEncounterFor } from './movement-truth.js';
import { fetchAndCacheObject } from './object-cache.js';
import { refuseUnported } from '../janus-engine/unported.js';
import type { BasePatchFrame } from '../janus-engine/compile/base-patch';
import { queueActorSave } from './applied-save-outbox';
import { cachedInventoryRows } from '../janus-engine/inventory/cached-inventory-trees';
import { withAsyncDeadline } from '../shared/async-deadline';

/** Ordinary edits preserve the held flag when their form has no visibility checkbox. */
export async function planInventoryEditAdmission(id: string, encounterId: string, desired?: Bit) {
  const item = inventoryCopies(upper(id))[0];
  if (!item) throw new Error('status-admission:target-unavailable');
  const encounter = record(actorEncounterFor(encounterId)) ?? {};
  const members = new Map<string, Row>();
  const readers = abilityTruthReaders(item, encounter, new Set<string>());
  const value = desired ?? readInventoryHidden(item, INVENTORY_HIDDEN_ID);
  const definitions = new Set([INVENTORY_HIDDEN_ID, ...statusRows(item, 'target').flatMap(status => {
    const origin = upper(readers.value(status, 'status_origin_id'));
    return [statusItemId(status), isSelfStatusOrigin(upper(readers.value(status, 'status_id')), origin) ? '' : origin];
  })]);
  try {
    await withAsyncDeadline(() => Promise.all([...definitions].filter(Boolean).map(async source => {
      const held = readers.definition(source);
      if (!record(held?.item_layer_types) && !record(held?.computed)) {
        const loaded = record(await fetchAndCacheObject(source));
        if (!loaded || upper(itemField(loaded, 'item_id')) !== upper(source))
          throw new Error('status-admission:definition-unavailable');
      }
    })), 15000);
  } catch { throw new Error('status-admission:definition-unavailable'); }
  try {
    const plan = planInventoryHidden(item, value, encounter, members, readers);
    return { inventory_hidden: value, hidden_plan: plan };
  } catch (error) {
    throw new Error('status-admission:' + (error instanceof Error ? error.message : String(error)));
  }
}

const upper = (v: unknown): string => String(v ?? '').trim().toUpperCase();
const flagOwners = new Map<string, { encounterId: string; actorId: string }>();
const pendingFlags = new Map<string, InventoryFlagCommand>();

function inventoryCopies(id: string): Row[] {
  const ft = window.FateTide;
  const copies = new Set<Row>();
  for (const cache of [ft?.objectCache, ft?.casterCache, ft?.encounterCache]) for (const root of cache?.values() ?? []) {
    for (const row of cachedInventoryRows(root)) if (inventoryId(row) === id) copies.add(row);
  }
  return [...copies];
}

function applyHiddenPlan(command: InventoryFlagCommand): void {
  for (const row of inventoryCopies(command.inventory_id)) {
    const statuses = record(row.statuses) ?? (row.statuses = {}) as Row;
    const removed = new Set(command.hidden_plan?.writes.filter(write => write.kind === 'status-remove').map(write => write.status_id));
    const held = statusRows(row, 'target').filter(status => !removed.has(upper(status.status_id)));
    for (const write of command.hidden_plan?.writes ?? []) if (write.kind === 'hidden-mark' && !held.some(status => upper(status.status_id) === write.status_id)) held.push({
      status_id: write.status_id, status_connected_id: write.status_connected_id,
      status_item_id: INVENTORY_HIDDEN_ID, status_target_id: command.inventory_id,
      status_caster_id: command.inventory_id, status_stack: 1,
    });
    statuses.target = held;
  }
}

window.addEventListener('ft:inventory-base-compiled', () => {
  for (const command of pendingFlags.values()) {
    if (command.kind === 'inspection-dc') { patchInspectionDc(command.inventory_id, command.after.value); }
    else if (command.kind === 'hidden') { applyHiddenPlan(command); paintHiddenButtons(command.inventory_id, command.after.value); }
    else { patchWaresCaches(command.inventory_id, command.after.value); paintWaresButtons(command.inventory_id, command.after.value); }
  }
  const id = document.querySelector<HTMLInputElement>('.inventory-container-form #inventory_id')?.value;
  const item = id ? inventoryCopies(upper(id))[0] : null;
  if (id && item && ![...pendingFlags.values()].some(command => command.kind === 'hidden' && command.inventory_id === upper(id)))
    paintHiddenButtons(id, readInventoryHidden(item, INVENTORY_HIDDEN_ID), false);
});

export interface InventoryFlagWireFrame {
  schema: typeof INVENTORY_FLAG_SCHEMA;
  operation_id: string;
  encounter_id: string;
  inventory_id: string;
  kind: InventoryFlagKind;
  possess_id: string | null;
  value: number | null;
  root_id?: string;
  provisional?: boolean;
  committed?: boolean;
  correction?: boolean;
  reason?: string;
  patch?: string;
  base_refresh?: BasePatchFrame;
  hidden_plan?: InventoryFlagCommand['hidden_plan'];
  actor_id?: string;
  before?: InventoryFlagCommand['before'];
  after?: InventoryFlagCommand['after'];
}

/** Frames already applied per operation (provisional → committed / correction dedup). */
const seen = new Map<string, Set<string>>();
function noteSeen(operationId: string, kind: string): boolean {
  const kinds = seen.get(operationId) ?? new Set<string>();
  if (kinds.has(kind) || (kind === 'provisional' && (kinds.has('committed') || kinds.has('correction')))) return false;
  kinds.add(kind); seen.set(operationId, kinds);
  if (seen.size > 256) seen.delete(seen.keys().next().value!);
  return true;
}

/** Patch the wares flag into every cached compiled copy of the row this page holds. */
function patchWaresCaches(inventoryId: string, value: number): number {
  let hits = 0;
  const ft = (window as unknown as { FateTide?: { objectCache?: unknown; casterCache?: unknown; encounterCache?: unknown } }).FateTide;
  const visited = new Set<Row>();
  for (const cache of [ft?.objectCache, ft?.casterCache, ft?.encounterCache]) {
    if (!(cache instanceof Map)) continue;
    for (const host of cache.values()) for (const row of cachedInventoryRows(host)) if (!visited.has(row)) {
      visited.add(row); hits += applyWaresToTree({ inventory: row.inventory }, inventoryId, value);
    }
  }
  return hits;
}

function patchInspectionDc(id: string, value: number): void {
  for (const item of inventoryCopies(id)) {
    const bucket = record(item.inventory);
    if (bucket) bucket.inventory_inspection_dc = value;
    else item.inventory_inspection_dc = value;
  }
}

/** Paint the open row's visibility controls; base delivery preserves the form draft. */
function paintHiddenButtons(inventoryId: string, value: number, syncCheckbox = true): void {
  const hide = document.getElementById('btn_hide_creature_' + inventoryId);
  const reveal = document.getElementById('btn_reveal_creature_' + inventoryId);
  if (hide) hide.style.display = value === 1 ? 'none' : 'block';
  if (reveal) reveal.style.display = value === 1 ? 'block' : 'none';
  const form = (hide ?? reveal)?.closest('.inventory-container-form');
  const checkbox = form?.querySelector<HTMLInputElement>('#inventory_hidden');
  if (checkbox && syncCheckbox) checkbox.checked = value === 1;
}
/** The open object card's wares pair for the row. */
function paintWaresButtons(inventoryId: string, value: number): void {
  const on = document.getElementById('btn_wares_on_inventory_' + inventoryId);
  const off = document.getElementById('btn_wares_off_inventory_' + inventoryId);
  if (on) on.style.display = value === 1 ? 'none' : 'block';
  if (off) off.style.display = value === 1 ? 'block' : 'none';
}

export function handleInventoryFlagTruth(frame: InventoryFlagWireFrame): void {
  if (frame?.schema !== INVENTORY_FLAG_SCHEMA || !frame.inventory_id || (frame.kind !== 'wares' && frame.kind !== 'hidden' && frame.kind !== 'inspection-dc')) return;
  const kind = frame.correction ? 'correction' : frame.committed ? 'committed' : 'provisional';
  if (!noteSeen(frame.operation_id, kind)) return;
  if (kind === 'committed') refreshSavedInventory(frame.base_refresh);
  if (frame.hidden_plan && frame.before && frame.after) applyHiddenPlan(frame as InventoryFlagWireFrame & InventoryFlagCommand);
  const iid = upper(frame.inventory_id);
  const saved = frame.kind === 'inspection-dc' ? frame.value : frame.value === 1 ? 1 : frame.value === 0 ? 0 : null;
  if (kind !== 'provisional') noteInventoryFlagFrame(frame.kind, iid, saved === null || !frame.possess_id ? null : { possess_id: frame.possess_id, value: saved });
  const pending = pendingInventoryFlag(frame.kind, iid);
  const value = pending && pending.operationId !== frame.operation_id ? pending.value : saved;
  if (value !== null) {
    if (frame.kind === 'wares') {
      patchWaresCaches(iid, value);
      paintWaresButtons(iid, value);
      // The PC sheet re-reads its tiles from the caster cache (its own optimistic paint wins while it is pending).
      if (frame.root_id) (window as unknown as { ftRepaintCasterFromCache?: (id: string) => void }).ftRepaintCasterFromCache?.(upper(frame.root_id));
    } else if (frame.kind === 'inspection-dc') {
      patchInspectionDc(iid, value);
    } else {
      paintHiddenButtons(iid, value);
    }
  }
  window.dispatchEvent(new CustomEvent('ft:inventory-flag-truth', { detail: { kind, frame } }));
}

/** Hydrate genuinely missing context. A rapid toggle reads the preceding local mark,
 * including its browser-assigned status identity, before either save answers. */
async function prepareHidden(command: InventoryFlagCommand) {
  try {
    const item = inventoryCopies(command.inventory_id)[0] ?? record(await fetchAndCacheObject(command.inventory_id));
    if (!item || inventoryId(item) !== command.inventory_id) throw new HandBack('inventory-hidden:item-not-held');
    const holder = upper(inventoryField(item, 'possess_id'));
    if (holder !== command.before.possess_id) throw new InventoryFlagPreparationError('inventory-hidden:holder-changed',
      { possess_id: holder, value: readInventoryHidden(item, INVENTORY_HIDDEN_ID) });
    let root: Row = item;
    const seen = new Set<string>();
    while (inventoryId(root)) {
      const holder = upper(inventoryField(root, 'possess_id'));
      if (!holder || seen.has(holder) || seen.size >= 40) throw new HandBack('inventory-hidden:root-not-held');
      seen.add(holder);
      const parent = record(window.FateTide?.casterCache?.get(holder)) ?? inventoryCopies(holder)[0] ?? record(await fetchAndCacheObject(holder));
      if (!parent) throw new HandBack('inventory-hidden:root-not-held');
      root = parent;
    }
    const eid = upper(ecId(root) ? ecLean(root, 'encounter_id') : encounterField(root, 'encounter_id'));
    if (!eid) throw new HandBack('inventory-hidden:encounter-not-held');
    if (!actorEncounterFor(eid)) await window.ftResyncEncounter?.(eid);
    const encounter = record(actorEncounterFor(eid));
    if (!encounter) throw new HandBack('inventory-hidden:encounter-not-held');
    const members = new Map(normalizePhpListish(encounter.encounter_creatures_arr).flatMap(value => {
      const member = record(value), id = ecId(member);
      return member && id ? [[id, member] as [string, Row]] : [];
    }));
    const readers = abilityTruthReaders(item, encounter, new Set(members.keys()));
    const definitions = new Set([INVENTORY_HIDDEN_ID, ...statusRows(item, 'target').flatMap(status =>
      [statusItemId(status), isSelfStatusOrigin(upper(status.status_id), upper(status.status_origin_id)) ? '' : upper(status.status_origin_id)]
        .filter(id => /^[0-9A-F-]{36}$/.test(id)))]);
    for (const id of definitions) {
      const definition = readers.definition(id);
      if (!record(definition?.item_layer_types) && !record(definition?.computed)) await fetchAndCacheObject(id);
    }
    await hydrateTurnTriggerDefinitions(item, encounter, [], [item]);
    const plan = planInventoryHidden(item, command.after.value === 1 ? 1 : 0, encounter, members, readers);
    flagOwners.set(command.operation_id, { encounterId: eid, actorId: ecId(root) || command.before.possess_id });
    return plan;
  } catch (error) {
    if (error instanceof InventoryFlagPreparationError) throw error;
    const reason = error instanceof HandBack ? error.reason : `inventory-hidden:${String(error)}`;
    refuseUnported(reason);
    throw new Error(reason);
  }
}

/** Out-of-bundle seam: prepare Hidden before the caller paints, then commit its explicit desired value. */
export function commitInventoryFlag(input: {
  kind: InventoryFlagKind; encounterId: string; inventoryId: string; actorId?: string | null; desired: number; seed: { value: number; possessId: string };
  onPrepared?: () => void;
}): Promise<InventoryFlagAnswer> {
  return queueInventoryFlag(input, fetch, prepareHidden, command => {
    const owner = flagOwners.get(command.operation_id) ?? { encounterId: input.encounterId, actorId: input.actorId || command.before.possess_id };
    flagOwners.delete(command.operation_id);
    pendingFlags.set(command.operation_id, command);
    if (command.kind === 'inspection-dc') patchInspectionDc(command.inventory_id, command.after.value);
    else if (command.kind === 'hidden') applyHiddenPlan(command);
    else {
      patchWaresCaches(command.inventory_id, command.after.value);
      paintWaresButtons(command.inventory_id, command.after.value);
    }
    const wire = { ...command, encounter_id: owner.encounterId,
      root_id: owner.actorId, possess_id: command.before.possess_id, value: command.after.value };
    const writes: ActivityWrite[] = command.kind === 'inspection-dc'
      ? [{ table: 'inventory', id: command.inventory_id, before: {}, after: { inventory_inspection_dc: command.after.value } }]
      : command.kind === 'wares'
      ? [{ table: 'inventory', id: command.inventory_id, before: {}, after: { inventory_wares: command.after.value } }]
      : (command.hidden_plan?.writes ?? []).map(write => ({ table: 'statuses', id: write.status_id, before: null,
        after: write.kind === 'status-remove' ? { status_deleted: 1 } : { status_item_id: INVENTORY_HIDDEN_ID,
          status_connected_id: write.status_connected_id, status_target_id: command.inventory_id, status_caster_id: command.inventory_id, status_stack: 1 } }));
    pushInventoryTruth('janus_inventory_flag', wire, inventoryAppliedFrame(wire, writes));
    return new Promise<InventoryFlagAnswer>(resolve => {
      queueActorSave({ ...owner, actorIds: [owner.actorId], command,
        send: async () => {
          const answer = await postInventoryFlag(command, (endpoint, init) => saveAppliedTruth(String(endpoint), command, init!));
          if (answer.status === 0 || answer.status >= 500) throw new Error(answer.error ?? 'Inventory flag save unavailable');
          if (answer.ok) { pendingFlags.delete(command.operation_id); resolve(answer); }
          return answer;
        },
        refused: async info => {
          pendingFlags.delete(command.operation_id);
          if (info.chained) { resolve({ ok: false, status: info.status, error: info.error }); return; }
          const saved = record(info.body?.saved);
          const value = saved ? Number(saved.value) : command.before.value;
          if (command.kind === 'inspection-dc') patchInspectionDc(command.inventory_id, value);
          else if (command.kind === 'wares') {
            patchWaresCaches(command.inventory_id, value); paintWaresButtons(command.inventory_id, value);
            window.ftRepaintCasterFromCache?.(owner.actorId);
          } else paintHiddenButtons(command.inventory_id, value);
          const answer: InventoryFlagAnswer = { ok: false, status: info.status, error: info.error,
            saved: saved ? { value, possess_id: upper(saved.possess_id) } : null };
          resolve(answer);
          window.displayWarning?.('The inventory change was refused; its current state was restored.');
          await window.ftResyncEncounter?.(owner.encounterId);
        },
      });
    });
  });
}

/**
 * Hide / reveal ONE inventory row from any surface that only knows the row (the archived PHP object card's inline buttons, the
 * authoring edit form): paint the pair, commit the explicit desired value, restore + warn on a refusal. The encounter may be
 * omitted — the server judges the GM against the encounter that governs the row. Resolves true when the row now holds `desired`.
 */
const hiddenPaintGeneration = new Map<string, number>();
export async function setInventoryHidden(inventoryId: string, desired: Bit, possessId: string, encounterId = ''): Promise<boolean> {
  const iid = upper(inventoryId);
  // Only the NEWEST click on this row may repaint after it settles (an older refusal must not undo a later paint).
  const gen = (hiddenPaintGeneration.get(iid) ?? 0) + 1;
  hiddenPaintGeneration.set(iid, gen);
  const answer = await commitInventoryFlag({ kind: 'hidden', encounterId, inventoryId: iid, desired, seed: { value: desired === 1 ? 0 : 1, possessId }, onPrepared: () => paintHiddenButtons(iid, desired) })
    .catch((error: unknown): InventoryFlagAnswer => ({ ok: false, status: 0, error: error instanceof Error ? error.message : String(error) }));
  if (answer.ok) return true;
  if (hiddenPaintGeneration.get(iid) === gen) paintHiddenButtons(iid, answer.saved ? answer.saved.value : (desired === 1 ? 0 : 1));
  const warn = (window as unknown as { displayWarning?: (message: string) => void }).displayWarning;
  warn?.(answer.status === 403 ? 'Only the GM can hide or reveal this object.'
    : answer.status === 409 ? 'That object changed elsewhere; its current state was restored.' : 'The change could not be saved: ' + String(answer.error || answer.status));
  return false;
}

Object.assign(window, { ftCommitInventoryFlag: commitInventoryFlag, ftSetInventoryHidden: setInventoryHidden,
  ftPlanInventoryEditAdmission: planInventoryEditAdmission });
